import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  useGetGrowthWheelByRoleIdQuery,
  useGetGrowthWheelAssessmentSessionsByUserIdQuery,
  useGetGrowthWheelWithEvaluationsByUserIdQuery,
} from '../../redux/growth-wheel.endpoints'
import { useGetAllRolesQuery } from '../../redux/role.endpoints'
import {
  setGrowthWheelData,
  setUserCompetenciesData,
  setLoadingGrowthWheel,
  setCurrentCompetency,
  setCurrentSubCompetency,
  evaluateFocusCompetenciesLimit,
  evaluateGrowthWheelCompletion,
  setAssessmentCompletion,
  setAssessmentSession,
  setAssessmentSessions,
  setSelectedAssessmentSessionId,
  setActiveAssessmentSessionId,
  setActiveGrowthWheelId,
  setCurrentAreasOfFocus,
  setIsReadOnly,
} from '../../redux/growthWheel.slice'
import { RootState } from '../../redux/store'
import { getGrowthWheelRoleId } from '../../types/Roles'
import { User } from '../../types/User'
import GrowthWheelDashboard from '../GrowthWheelDashboard/GrowthWheelDashboard'
import GrowthWheelCompetencyView from '../GrowthWheelCompetencyView/GrowthWheelCompetencyView'
import GrowthWheelSubCompetencyAssessment from '../GrowthWheelSubCompetencyView/GrowthWheelSubCompetencyView'
import Loader from '../../components/Loader/Loader'

const GrowthWheel = ({ user }: { user: User }) => {
  const dispatch = useDispatch()
  const { data: guilds } = useGetAllRolesQuery()
  const roleId = getGrowthWheelRoleId(user, guilds)
  const isResolvingGuildMasterRole = Boolean(user.isGuildMaster) && !guilds

  const { data: growthWheelData, isLoading } = useGetGrowthWheelByRoleIdQuery(
    { roleId },
    {
      skip: isResolvingGuildMasterRole,
      refetchOnMountOrArgChange: true,
    },
  )
  const { data: assessmentSessionsData } =
    useGetGrowthWheelAssessmentSessionsByUserIdQuery(
      { userId: user.id },
      { refetchOnMountOrArgChange: true },
    )
  const assessmentSessions = useMemo(
    () => assessmentSessionsData ?? [],
    [assessmentSessionsData],
  )
  const activeSessionId = useMemo(
    () =>
      assessmentSessions.find(
        session =>
          session.status === 'in_progress' &&
          session.growthWheelStatus === 'active',
      )?.id ?? null,
    [assessmentSessions],
  )
  const selectedSessionId = useSelector(
    (state: RootState) => state.growthWheel.selectedAssessmentSessionId,
  )
  const viewingSessionId =
    selectedSessionId ?? activeSessionId ?? assessmentSessions[0]?.id ?? null
  const { data: userAssessmentData } =
    useGetGrowthWheelWithEvaluationsByUserIdQuery(
      { userId: user.id, assessmentSessionId: viewingSessionId },
      { refetchOnMountOrArgChange: true },
    )
  const userCompetenciesData = userAssessmentData?.competencies
  const viewingSession =
    assessmentSessions.find(session => session.id === viewingSessionId) ?? null
  const isViewingReadOnly =
    viewingSession?.status === 'completed' ||
    (viewingSessionId != null && viewingSessionId !== activeSessionId)

  const currentCompetency = useSelector(
    (state: RootState) => state.growthWheel.currentCompetency,
  )
  const currentSubCompetency = useSelector(
    (state: RootState) => state.growthWheel.currentSubCompetency,
  )

  useEffect(() => {
    dispatch(
      setGrowthWheelData(
        userAssessmentData?.growthWheel ?? growthWheelData ?? null,
      ),
    )
  }, [dispatch, growthWheelData, userAssessmentData])

  useEffect(() => {
    dispatch(setAssessmentSessions(assessmentSessions))
    dispatch(setActiveAssessmentSessionId(activeSessionId))
    dispatch(setActiveGrowthWheelId(growthWheelData?.id ?? null))
  }, [activeSessionId, assessmentSessions, dispatch, growthWheelData])

  useEffect(() => {
    dispatch(setUserCompetenciesData(userCompetenciesData ?? []))
    dispatch(evaluateFocusCompetenciesLimit())
    dispatch(evaluateGrowthWheelCompletion())
    dispatch(setCurrentAreasOfFocus())
    dispatch(
      setAssessmentCompletion({
        isCompleted: userAssessmentData?.isCompleted ?? false,
        completedAt: userAssessmentData?.completedAt ?? null,
      }),
    )
    dispatch(
      setAssessmentSession(userAssessmentData?.assessmentSession ?? null),
    )
    dispatch(setIsReadOnly(Boolean(isViewingReadOnly)))
  }, [dispatch, userCompetenciesData, userAssessmentData, isViewingReadOnly])

  useEffect(() => {
    dispatch(setLoadingGrowthWheel(isLoading || isResolvingGuildMasterRole))
  }, [dispatch, isLoading, isResolvingGuildMasterRole])

  useEffect(() => {
    if (!userCompetenciesData?.length) return
    if (currentCompetency) {
      const freshCompetency = userCompetenciesData.find(
        c => c.id === currentCompetency.id,
      )
      if (freshCompetency) {
        dispatch(setCurrentCompetency(freshCompetency))
        if (currentSubCompetency) {
          const freshSub = freshCompetency.subCompetencies?.find(
            s => s.id === currentSubCompetency.id,
          )
          if (freshSub) dispatch(setCurrentSubCompetency(freshSub))
        }
      }
    }
  }, [userCompetenciesData, currentCompetency, currentSubCompetency, dispatch])

  useEffect(() => {
    if (
      selectedSessionId != null &&
      !assessmentSessions.some(session => session.id === selectedSessionId)
    ) {
      dispatch(setSelectedAssessmentSessionId(null))
    }
  }, [assessmentSessions, dispatch, selectedSessionId])

  const displayAssessmentView = currentSubCompetency && currentCompetency
  const displayCompetencyView = currentCompetency && !currentSubCompetency
  const displayDashboardView = !currentCompetency && growthWheelData

  if (displayAssessmentView) {
    return <GrowthWheelSubCompetencyAssessment subjectUserId={user.id} />
  }
  if (displayCompetencyView) {
    return <GrowthWheelCompetencyView subjectUserId={user.id} />
  }
  if (displayDashboardView) {
    return <GrowthWheelDashboard userId={user.id} />
  }
  return <Loader />
}

export default GrowthWheel
